
import {Component, Vue} from "vue-property-decorator";
import {Getter} from "vuex-class";
import { CONNECTED_BOX_PROVIDERS } from "@/utils/connectedBoxes";
import QrCodeScannerWrapper from "@/components/qrcode/QrCodeScannerWrapper.vue";
import RelaysAutocomplete from "@/components/relays/RelaysAutocomplete.vue";
import { CONTEXT_TYPES } from "@/store/modules/context";

enum TABS {
  LOCATION_SCANNER = "locationScanner",
  CONNECTED_BOX_ONBOARDING = "connectedBoxOnboarding",
}

@Component({
    components: {
      RelaysAutocomplete,
      QrCodeScannerWrapper,
    },
})
export default class QrCodeLocation extends Vue {

    @Getter private currentUser: any;
    @Getter private isAdmin: boolean;
    @Getter private envCompanyId: number|null;
    @Getter private isMobile: boolean;
    @Getter private canDoInEnvCompany: (right: string) => boolean;

    private currentTab: string = this.displayedTabs[0];
    private loadingQrCodeStreamer: boolean = false;
    private relayId: number = null;
    private canUseMasterlocks: boolean = false;

    get displayedTabs(): string[] {
      return [{
        name: TABS.LOCATION_SCANNER,
        isDisplayed: true,
      }, {
        name: TABS.CONNECTED_BOX_ONBOARDING,
        isDisplayed: this.canUseMasterlocks
          && this.canDoInEnvCompany("EDIT_BOX"),
      }]
        .filter((tab) => tab.isDisplayed || this.isAdmin)
        .map((tab) => tab.name);
    }

    get displayQrCodeStreamer(): boolean {
      switch (this.currentTab) {
        case TABS.LOCATION_SCANNER:
          return true;
        case TABS.CONNECTED_BOX_ONBOARDING:
          return !!this.relayId;
        default :
          return false;
      }
    }

    get displayRelaysAutocomplete(): boolean {
      return this.currentTab === TABS.CONNECTED_BOX_ONBOARDING && !!this.userCompany?.id;
    }

    get userCompany(): any|undefined {
      return this.currentUser?.company;
    }

    private mounted() {
      this.fetchCompanyOptions();
    }

    private async fetchCompanyOptions(): Promise<void> {
      if (this.envCompanyId) {
        this.canUseMasterlocks = await this.$store.dispatch("options/readOptionValueByCompany", {
          companyId: this.envCompanyId,
          optionTypeLabel: "masterlocks",
        });
      }
    }

    private changeTab(tab): void {
        this.currentTab = tab;
    }

    private changeRelay({ selectedRelay }): void {
        this.relayId = selectedRelay?.id;
    }

    private onDecode(decodedString: string) {
        if (decodedString) {
            this.loadingQrCodeStreamer = true;
            this.$nextTick().then(() =>
                this.onDecodePromise(decodedString).finally(() => {
                    this.loadingQrCodeStreamer = false;
                }),
            );
        }
    }

    private onDecodePromise(decodedString: string): Promise<any> {
      switch (this.currentTab) {
        case "locationScanner" :
          return this.getAndSetContext(decodedString);
        case "connectedBoxOnboarding" :
          return this.createMasterlock(decodedString);
      }
    }

    private getAndSetContext(decodedString: string): Promise<any> {
      return this.$store.dispatch("context/getByToken", {
        token: decodedString,
      }).then(async (res: any) => {
        // If the scanned qr code is a location, and no key is linked to it, throw error
        if (res?.type === CONTEXT_TYPES.LOCATION && !res?.keyId) {
          this.$store.commit("alerts/displayError", {
            msg: this.$i18n?.t("alerts.error.noKeyLinked"),
          });
          return;
        }
        // Store context result if it exists
        if (res?.id) {
          this.$store.dispatch("context/updateContext", {
            type: res.type,
            id: res.id,
            name: res.name,
            token: decodedString,
            provider: res.provider,
          });
        }
        // If the context belong to a company, update global env companyId
        if (res?.companyId) {
          // Update global env companyId with current entity company
          await this.$store.dispatch("changeEnvCompanyId", res.companyId);
        }
        // If a location was scanned, open context for a single key
        if (res?.type === CONTEXT_TYPES.LOCATION) {
          return this.$router.push({
            name: "key",
            params: {
              id: res.keyId,
            },
          });
        }
        // If a box was scanned, open context for all keys related to this box
        if (res?.type === CONTEXT_TYPES.BOX) {
          return this.$router.push({
            name: "keys",
          });
        }
      }).catch((error) => {
        this.$store.commit("alerts/displayHttpError", { error });
      });
    }

    private createMasterlock(decodedString: string): Promise<any> {
      const [externalId, activationCode] = decodedString.split(":");
      if (!externalId || !activationCode) {
        // If stream didn't work, display error message and re-activate it
        return new Promise((resolve, reject) => resolve(
          this.$store.commit("alerts/displayError", {
            msg: this.$i18n?.t("connectedBoxOnboarding.unknownQrCode"),
          })),
        );
      } else {
        return this.$store.dispatch("relays/addBox", {
          relayId: this.relayId,
          box: {
            externalId,
            activationCode,
            provider: CONNECTED_BOX_PROVIDERS.SNAPACCESS,
          },
        })
          .then(() => this.$store.commit("alerts/displaySuccess", {
              msg: this.$i18n?.t("connectedBox.initializationLatency"),
          }))
          .catch((err) => this.$store.commit("alerts/displayError", {
              msg: err?.response?.data?.error || err,
          }));
      }
    }
}
